// src/components/TestDrive.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./TestDrive.css";

const BRANDS = {
  Porsche: ["911", "Cayenne", "Macan", "Panamera", "Taycan"],
  BMW: ["3 Series", "5 Series", "X5", "i4", "iX", "M3", "M5"],
  Mercedes: ["A-Class", "C-Class", "E-Class", "S-Class", "G-Class", "AMG GT"],
  Audi: ["A3", "A4", "A6", "A8", "Q5", "Q7", "RS7", "R8", "e-tron GT"],
  Ferrari: ["F8 Tributo", "SF90 Stradale", "812 Superfast", "Roma", "Portofino"],
  "Aston Martin": ["DB11", "Vantage", "DBS Superleggera", "DBX"],
  Lamborghini: ["Aventador", "Huracán", "Revuelto", "Urus"],
  Mustang: ["Mustang GT", "Mach 1", "Shelby GT500", "Mach-E"],
};

const MODEL_PRICES = {
  "911": 145, "Cayenne": 125, "Macan": 95, "Panamera": 165, "Taycan": 155,
  "3 Series": 48, "5 Series": 72, "X5": 92, "i4": 75, "iX": 115, "M3": 98, "M5": 175,
  "A-Class": 42, "C-Class": 58, "E-Class": 78, "S-Class": 165, "G-Class": 185, "AMG GT": 245,
  "A3": 38, "A4": 48, "A6": 62, "A8": 135, "Q5": 65, "Q7": 88, "RS7": 195, "R8": 285, "e-tron GT": 185,
  "F8 Tributo": 425, "SF90 Stradale": 750, "812 Superfast": 580, "Roma": 350, "Portofino": 385,
  "DB11": 325, "Vantage": 285, "DBS Superleggera": 485, "DBX": 385,
  "Aventador": 650, "Huracán": 385, "Revuelto": 850, "Urus": 425,
  "Mustang GT": 78, "Mach 1": 88, "Shelby GT500": 125, "Mach-E": 72
};

const COLORS = ["Jet Black", "Phantom Black", "Arctic White", "Glacier Silver", "Carrara White", "Racing Red", "Rosso Corsa", "Blu Nova", "Ocean Blue", "Sage Green", "Bronze", "Desert Yellow", "Carbon Fiber", "Pearl White", "Graphite Grey", "Metallic Blue", "Sunset Orange", "Midnight Purple"];

const VARIANTS = ["Base", "Standard", "Sport", "Sport Plus", "Luxury", "Turbo", "Turbo Plus", "Limited"];

const FUEL_TYPES = ["Petrol", "Diesel", "Electric", "Hybrid"];
const TRANSMISSIONS = ["Automatic", "Manual"];

const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`td-toast toast-${type}`}>
      <span className="toast-icon">{type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}</span>
      <span>{message}</span>
    </div>
  );
};

export default function TestDrive() {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState({
    brand: "", model: "", variant: "", color: "", fuel: "", transmission: "",
    date: "", time: "", modeLocation: "Dealership", address: "", fullName: "",
    email: "", phone: "", gender: "", drivingLicense: "yes", updates: false,
    specialRequest: "", chauffeur: false, vrExperience: false, extendedDrive: false,
  });

  const showToast = (message, type = 'info') => setToast({ message, type });

  useEffect(() => {
    setLoggedIn(!!localStorage.getItem("revoraUser"));
    if (localStorage.getItem("revoraMembership") === "Premium") setIsPremium(true);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("revoraPrefill");
    if (saved) {
      const data = JSON.parse(saved);
      setMode(data.mode === "custom" ? "custom" : "direct");
      setForm((prev) => ({
        ...prev, brand: data.brand || "", model: data.model || "", variant: data.variant || "",
        color: data.customization?.exteriorColor || "", fuel: data.customization?.fuel || "",
        transmission: data.customization?.transmission || "",
      }));
      localStorage.removeItem("revoraPrefill");
    }
  }, []);

  useEffect(() => {
    if (form.brand && BRANDS[form.brand] && form.model && !BRANDS[form.brand].includes(form.model)) {
      setForm((s) => ({ ...s, model: "" }));
    }
  }, [form.brand, form.model]);

  const modelOptions = useMemo(() => (form.brand ? BRANDS[form.brand] : []), [form.brand]);
  const getEstimatedPrice = () => form.model ? MODEL_PRICES[form.model] || 50 : 0;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const submitBooking = (bookingType) => {
    if (!loggedIn) {
      showToast("Please login to book a test drive", "error");
      setTimeout(() => navigate("/login"), 1500);
      return;
    }
    if (!form.brand || !form.model || !form.date || !form.time || !form.fullName || !form.email || !form.phone) {
      showToast("Please fill all required fields", "error");
      return;
    }

    const payload = { bookingType, ...form, estimatedPrice: getEstimatedPrice() };
    const stored = JSON.parse(localStorage.getItem("revoraActivity") || "[]");
    stored.unshift({
      title: `${form.brand} ${form.model} — ${bookingType === "custom" ? "Customised" : "Direct"} Test Drive`,
      description: `Date: ${form.date} ${form.time} | Est: ₹${getEstimatedPrice()} Lakhs`,
      status: "Confirmed", createdAt: new Date().toISOString(), payload,
    });
    localStorage.setItem("revoraActivity", JSON.stringify(stored));
    showToast("Test drive booking confirmed!", "success");
    setTimeout(() => navigate("/account"), 2000);
  };

  return (
    <section className="testdrive-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="testdrive-wrapper glass-wide">
        <h1 className="td-title">Book Your Test Drive</h1>
        <p className="td-subtitle">Choose your driving style — fast & easy direct test drive or a detailed customised experience.</p>

        <div className="td-mode-row">
          <button className={`td-mode-btn ${mode === "direct" ? "active" : ""}`} onClick={() => setMode("direct")}>Direct Test Drive</button>
          <button className={`td-mode-btn ${mode === "custom" ? "active" : ""}`} onClick={() => setMode("custom")}>Customised Test Drive</button>
        </div>

        {mode && (
          <div className={`forms-grid ${mode === "custom" ? "custom-mode" : "direct-mode"}`}>
            <form className="td-form" onSubmit={(e) => { e.preventDefault(); submitBooking(mode); }}>
              <div className="section">
                <h3>{mode === "direct" ? "Quick Car Selection" : "Detailed Customisation"}</h3>
                <label>Brand *</label>
                <select name="brand" value={form.brand} onChange={handleChange} required>
                  <option value="">Select Brand</option>
                  {Object.keys(BRANDS).map((b) => <option key={b} value={b}>{b}</option>)}
                </select>

                <label>Model *</label>
                <select name="model" value={form.model} onChange={handleChange} required>
                  <option value="">Select Model</option>
                  {modelOptions.map((m) => <option key={m} value={m}>{m} (₹{MODEL_PRICES[m]} Lakhs)</option>)}
                </select>

                {mode === "custom" && (
                  <>
                    <label>Variant</label>
                    <select name="variant" value={form.variant} onChange={handleChange}>
                      <option value="">Select Variant</option>
                      {VARIANTS.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <div className="row-2">
                      <div><label>Color</label><select name="color" value={form.color} onChange={handleChange}><option value="">Choose Color</option>{COLORS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                      <div><label>Fuel Type</label><select name="fuel" value={form.fuel} onChange={handleChange}><option value="">Select Fuel</option>{FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}</select></div>
                    </div>
                    <label>Transmission</label>
                    <select name="transmission" value={form.transmission} onChange={handleChange}>
                      <option value="">Select Transmission</option>
                      {TRANSMISSIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </>
                )}
              </div>

              {isPremium ? (
                <div className="section premium-section">
                  <h3>💎 Premium Experience</h3>
                  <p className="premium-note">Exclusive options for Revora Premium members</p>
                  <label><input type="checkbox" name="chauffeur" checked={form.chauffeur} onChange={handleChange} /> Chauffeur Pickup & Drop</label>
                  <label><input type="checkbox" name="vrExperience" checked={form.vrExperience} onChange={handleChange} /> Virtual Reality Configurator</label>
                  <label><input type="checkbox" name="extendedDrive" checked={form.extendedDrive} onChange={handleChange} /> Extended Drive (2 Hours)</label>
                </div>
              ) : (
                <div className="section premium-locked">
                  <h3>💎 Premium Features Locked</h3>
                  <p>Upgrade to <span onClick={() => navigate("/join-premium")}>Revora Premium</span> to unlock exclusive experiences.</p>
                </div>
              )}

              <div className="section">
                <h3>Drive Details</h3>
                <div className="row-2">
                  <div><label>Date *</label><input name="date" type="date" value={form.date} onChange={handleChange} required /></div>
                  <div><label>Time *</label><input name="time" type="time" value={form.time} onChange={handleChange} required /></div>
                </div>
              </div>

              <div className="section">
                <h3>Your Information</h3>
                <label>Full Name *</label><input name="fullName" type="text" value={form.fullName} onChange={handleChange} required />
                <label>Email *</label><input name="email" type="email" value={form.email} onChange={handleChange} required />
                <label>Phone *</label><input name="phone" type="tel" value={form.phone} onChange={handleChange} required />
              </div>

              <div className="section">
                <h3>Drive Mode</h3>
                <label>Mode</label>
                <select name="modeLocation" value={form.modeLocation} onChange={handleChange}>
                  <option value="Dealership">At Dealership</option>
                  <option value="At Home">At Home</option>
                </select>
                {form.modeLocation === "At Home" && (<label>Address *<input name="address" type="text" value={form.address} onChange={handleChange} required /></label>)}
              </div>

              <div className="section">
                <div className="row-2">
                  <div><label>Gender</label><select name="gender" value={form.gender} onChange={handleChange}><option value="">Select</option><option value="Female">Female</option><option value="Male">Male</option><option value="Other">Other</option></select></div>
                  <div><label>Driving License</label><select name="drivingLicense" value={form.drivingLicense} onChange={handleChange}><option value="yes">Yes</option><option value="no">No</option></select></div>
                </div>
                <label><input type="checkbox" name="updates" checked={form.updates} onChange={handleChange} /> Send me updates about new models or offers</label>
                <label>Any special request?</label>
                <textarea name="specialRequest" value={form.specialRequest} onChange={handleChange} placeholder="Anything to note?" />
              </div>

              <div className="action-row"><button type="submit" className="confirm-btn">Confirm {mode === "custom" ? "Customised" : "Direct"} Booking</button></div>
            </form>

            <aside className="td-summary">
              <div className="summary-card">
                <h3>Booking Summary</h3>
                <p><strong>Type:</strong> {mode === "custom" ? "Customised" : "Direct"} Test Drive</p>
                <p><strong>Car:</strong> {form.brand ? `${form.brand} ${form.model}` : "-"}</p>
                <p><strong>Variant:</strong> {form.variant || "-"}</p>
                <p><strong>Color:</strong> {form.color || "-"}</p>
                <p><strong>Fuel:</strong> {form.fuel || "-"}</p>
                <p><strong>Transmission:</strong> {form.transmission || "-"}</p>
                <p><strong>Date & Time:</strong> {form.date ? `${form.date} ${form.time}` : "-"}</p>
              </div>
              <div className="price-summary-card">
                <h3>Estimated Price</h3>
                <div className="price-display">
                  <span>{form.model ? `₹${getEstimatedPrice()} Lakhs` : "Select a model"}</span>
                </div>
                <p className="price-note">*Base price. Customizations may vary.</p>
              </div>
            </aside>
          </div>
        )}
      </div>
    </section>
  );
}